var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { State, Action, Selector } from '@ngxs/store';
import { StoryLabelsStateModel } from './story.labels.state.model';
import { AddLabel, RemoveLabel, SetLabels, SelectLabel, SetLabel, RemoveAllChapterLabels, CopyLabels, RemoveAllLabels, ResetLabels, ConvertLabels, AddContextLabel, EnableContextLabel, RemoveContextLabel, SetLabelsShown, SetAllLabelsShown, SetLabelsCollapsed, SetLabelsDraggable } from './story.labels.actions';
import { uuid } from 'src/app/shared/uuid';
import { StoryChaptersState } from '../chapters/story.chapters.state';
import { StoryChaptersStateModel } from '../chapters/story.chapters.state.model';
import { LabelsService } from '../../labels.service';
import { LabelRecipe } from '../../models/labels/label-recipe.enum';
import { FlyToChapterErrandModel } from '../../models/errands/fly-to-chapter-errand.model';
import { FlyToObjectErrandModel } from '../../models/errands/fly-to-object-errand.model';
import { cloneDeep } from 'lodash';
import { LabelCollapseModeEnum } from '../../models/labels/label-collapse-mode';
const initialState = {
    enableContextLabel: {
        enable: true,
        enableInfo: true,
        enableActions: true
    },
    loading: false,
    labelsCollapsed: false,
    labelsCollapsedMode: LabelCollapseModeEnum.None,
    labels: [],
    contextLabel: null,
    selectedLabel: null,
    draggable: {
        pin: true,
        tag: true
    }
};
let StoryLabelsState = class StoryLabelsState {
    constructor(labelsService) {
        this.labelsService = labelsService;
    }
    /**
     * Selectors
     */
    static getLabels(state) {
        return state.labels;
    }
    static getLabelsCollapsed(state) {
        return state.labelsCollapsed;
    }
    static getLabelsCollapsedMode(state) {
        return state.labelsCollapsedMode;
    }
    static getSelectedLabelId(state) {
        return state.selectedLabel;
    }
    static getSelectedLabel(state) {
        return state.labels.find(label => label.id === state.selectedLabel) || null;
    }
    static getContextLabel(state) {
        return state.contextLabel;
    }
    static getChapterLabels(state, storyChaptersState) {
        return state.labels.filter(label => label.chapterId === storyChaptersState.selectedChapter);
    }
    static getEnableContextLabel(state) {
        return state.enableContextLabel.enable;
    }
    static getEnableContextLabelInfo(state) {
        return state.enableContextLabel.enableInfo;
    }
    static getEnableContextLabelActions(state) {
        return state.enableContextLabel.enableActions;
    }
    static getLabelsDraggable(state) {
        return state.draggable;
    }
    /**
     * Actions
     */
    setLabels(context, action) {
        context.patchState({
            labels: action.labels
        });
    }
    setLabel(context, action) {
        const labels = context.getState().labels;
        const labelIndex = labels.findIndex((_label) => _label.id === action.label.id);
        labels[labelIndex] = Object.assign({}, action.label);
        context.patchState({
            labels: [...labels]
        });
    }
    addLabel(context, action) {
        action.label.id = action.label.id || uuid.idWithPrefix('label');
        const labels = context.getState().labels;
        context.patchState({
            labels: [...labels, Object.assign({}, action.label)]
        });
    }
    addContextLabel(context, action) {
        const label = Object.assign({}, action.label);
        label.id = 'context-label';
        context.patchState({
            contextLabel: label
        });
    }
    copyLabels(context, action) {
        const labels = context.getState().labels;
        const sourceLabels = labels.filter(label => label.chapterId === action.sourceChapter.id);
        const targetLabels = sourceLabels.map(label => {
            return Object.assign({}, (cloneDeep(label)), { id: uuid.idWithPrefix('label'), chapterId: action.targetChapter.id });
        });
        context.patchState({
            labels: [...labels, ...targetLabels]
        });
    }
    removeLabel(context, action) {
        const labels = context.getState().labels
            .filter((label) => {
            return label.id !== action.label.id;
        });
        context.patchState({
            labels: [...labels]
        });
        Human.scene.markers.delete(action.label.id);
    }
    removeContextLabel(context, action) {
        context.patchState({
            contextLabel: null
        });
        Human.scene.markers.delete('context-label');
    }
    removeAllLabels(context, action) {
        const labels = context.getState().labels;
        // Remove labels from the engine
        labels.forEach((label) => {
            Human.scene.markers.delete(label.id);
        });
        // Update application state
        context.patchState({
            labels: []
        });
    }
    removeAllChapterLabels(context, action) {
        const labels = context.getState().labels;
        const chapterLabels = labels.filter(label => label.chapterId === action.chapter.id);
        const filteredLabels = labels.filter(label => label.chapterId !== action.chapter.id);
        // Remove labels from the engine
        chapterLabels.forEach((label) => {
            Human.scene.markers.delete(label.id);
        });
        // Update application state
        context.patchState({
            labels: [...filteredLabels]
        });
    }
    selectLabel(context, action) {
        context.patchState({
            selectedLabel: action.label ? action.label.id : null
        });
    }
    resetLabels(context, action) {
        context.dispatch(new RemoveAllLabels());
        context.dispatch(new RemoveContextLabel());
        context.setState(initialState);
    }
    convertLabels(context, action) {
        const labels = context.getState().labels;
        action.labels.forEach(label => {
            const index = labels.findIndex(_label => _label.id === label.id);
            switch (action.type) {
                case LabelRecipe.StudioNext:
                    labels[index] = this.labelsService.makeDefaultStudioLabel(labels[index]);
                    if (label.objectId) {
                        labels[index].states[0].errands[0] = new FlyToChapterErrandModel();
                        labels[index].states[1].errands[0] = new FlyToObjectErrandModel(label.objectId);
                    }
                    break;
                case LabelRecipe.Annotation:
                    labels[index] = this.labelsService.makeAnnotationLabel(labels[index]);
                    break;
            }
        });
        context.patchState({
            labels: [...labels]
        });
    }
    enableContextLabel(context, action) {
        context.patchState({
            enableContextLabel: Object.assign({}, action)
        });
    }
    setLabelsShown(context, { labels: actionLabels, shown }) {
        // set shown state for each update label
        const labels = context.getState().labels
            .map((label) => {
            const doUpdate = actionLabels.some((actionLabel) => actionLabel.id === label.id);
            return Object.assign({}, label, { shown: doUpdate ? shown : label.shown });
        });
        context.patchState({
            labels: [...labels]
        });
    }
    setAllLabelsShown(context, { shown }) {
        const labels = context.getState().labels;
        context.dispatch(new SetLabelsShown(labels, shown));
    }
    setLabelsCollapsed(context, { collapsed, collapsedMode: mode }) {
        context.patchState({
            labelsCollapsed: collapsed,
            labelsCollapsedMode: mode
        });
    }
    setLabelsDraggable(context, action) {
        context.patchState({
            draggable: Object.assign({}, action.draggable)
        });
    }
};
__decorate([
    Action(SetLabels),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetLabels]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "setLabels", null);
__decorate([
    Action(SetLabel),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetLabel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "setLabel", null);
__decorate([
    Action(AddLabel),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, AddLabel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "addLabel", null);
__decorate([
    Action(AddContextLabel),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, AddContextLabel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "addContextLabel", null);
__decorate([
    Action(CopyLabels),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, CopyLabels]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "copyLabels", null);
__decorate([
    Action(RemoveLabel),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, RemoveLabel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "removeLabel", null);
__decorate([
    Action(RemoveContextLabel),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, RemoveContextLabel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "removeContextLabel", null);
__decorate([
    Action(RemoveAllLabels),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, RemoveLabel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "removeAllLabels", null);
__decorate([
    Action(RemoveAllChapterLabels),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, RemoveAllChapterLabels]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "removeAllChapterLabels", null);
__decorate([
    Action(SelectLabel),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SelectLabel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "selectLabel", null);
__decorate([
    Action(ResetLabels),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ResetLabels]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "resetLabels", null);
__decorate([
    Action(ConvertLabels),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, ConvertLabels]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "convertLabels", null);
__decorate([
    Action(EnableContextLabel),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, EnableContextLabel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "enableContextLabel", null);
__decorate([
    Action(SetLabelsShown),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetLabelsShown]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "setLabelsShown", null);
__decorate([
    Action(SetAllLabelsShown),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetAllLabelsShown]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "setAllLabelsShown", null);
__decorate([
    Action(SetLabelsCollapsed),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetLabelsCollapsed]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "setLabelsCollapsed", null);
__decorate([
    Action(SetLabelsDraggable),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [Object, SetLabelsDraggable]),
    __metadata("design:returntype", void 0)
], StoryLabelsState.prototype, "setLabelsDraggable", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getLabels", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getLabelsCollapsed", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getLabelsCollapsedMode", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getSelectedLabelId", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getSelectedLabel", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getContextLabel", null);
__decorate([
    Selector([StoryChaptersState]),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel, StoryChaptersStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getChapterLabels", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getEnableContextLabel", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getEnableContextLabelInfo", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getEnableContextLabelActions", null);
__decorate([
    Selector(),
    __metadata("design:type", Function),
    __metadata("design:paramtypes", [StoryLabelsStateModel]),
    __metadata("design:returntype", void 0)
], StoryLabelsState, "getLabelsDraggable", null);
StoryLabelsState = __decorate([
    State({
        name: 'labels',
        defaults: initialState
    }),
    __metadata("design:paramtypes", [LabelsService])
], StoryLabelsState);
export { StoryLabelsState };
